import { Link, useRouterState } from '@tanstack/react-router'
import { PortfolioNav, buildPortfolioProducts, type PortfolioNavLink } from '@saas-pages/shared/public/app-shell'
import {
  resolveUtilitySourceToolKey,
  type UtilityAnalyticsAttributionRoute,
} from '@saas-pages/shared/public/registry'
import { routePath, type RouteKey } from '@/seo/routes'
import { useT, type Translations } from '@/shared/i18n'
import { ThemeSwitcher } from '@/shared/theme/theme'
import { LangSwitcher } from '@/widgets/lang-switcher/LangSwitcher'

const DATEIFY_NAV_KEYS = [
  'calendar',
  'shift-calendar',
  'timestamp',
  'add-subtract-date',
  'date-difference',
  'business-days',
  'age-calculator',
  'days-until-date',
  'week-number',
  'time-zones',
  'timer',
  'pomodoro',
  'stopwatch',
  'blog',
] as const satisfies readonly RouteKey[]

export function UmbrellaNav() {
  const t = useT()
  const pathname = useRouterState({ select: (state) => state.location.pathname })
  const navItems: PortfolioNavLink[] = DATEIFY_NAV_KEYS.map((key) => ({
    key,
    label: dateifyNavLabel(key, t),
    href: routePath[key],
  }))

  return (
    <PortfolioNav
      brandLabel="dateify.ru"
      brandHref="/"
      products={buildPortfolioProducts('dateify')}
      items={navItems}
      sourceToolKey={resolveUtilitySourceToolKey(DATEIFY_TOOL_ATTRIBUTION, pathname)}
      actions={(
        <>
          <ThemeSwitcher />
          <LangSwitcher />
        </>
      )}
      renderLink={(link, className, children) => (
        <Link
          to={link.href}
          className={className}
          activeProps={
            link.key === 'brand'
              ? undefined
              : { className: 'bg-accent text-foreground' }
          }
        >
          {children}
        </Link>
      )}
    />
  )
}

const DATEIFY_TOOL_ATTRIBUTION = DATEIFY_NAV_KEYS
  .filter((key) => key !== 'blog')
  .map((key) => ({
    path: routePath[key],
    analyticsKey: key === 'time-zones' ? 'time-zone-converter' : key,
  })) satisfies readonly UtilityAnalyticsAttributionRoute[]

function dateifyNavLabel(key: (typeof DATEIFY_NAV_KEYS)[number], t: Translations): string {
  switch (key) {
    case 'calendar':
      return t.nav.calendar
    case 'shift-calendar':
      return t.nav.shiftCalendar
    case 'timestamp':
      return t.nav.timestamp
    case 'add-subtract-date':
      return t.nav.addSubtractDate
    case 'date-difference':
      return t.nav.dateDifference
    case 'business-days':
      return t.nav.businessDays
    case 'age-calculator':
      return t.nav.ageCalculator
    case 'days-until-date':
      return t.nav.daysUntilDate
    case 'week-number':
      return t.nav.weekNumber
    case 'time-zones':
      return t.nav.timeZones
    case 'timer':
      return t.nav.timer
    case 'pomodoro':
      return t.nav.pomodoro
    case 'stopwatch':
      return t.nav.stopwatch
    case 'blog':
      return t.nav.blog
  }
}
